import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { COMPANY_INFO as STATIC_COMPANY_INFO, MAIN_GALLERIES, SERVICES } from '../data/siteData';
import { useCompanyInfo } from '../lib/cms';

export default function Header() {
  const { companyInfo: COMPANY_INFO } = useCompanyInfo();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'entreprenoer' | 'belaegningHave' | 'vognmand' | 'galleri' | null
  const [mobileSectionOpen, setMobileSectionOpen] = useState({
    entreprenoer: true,
    belaegningHave: true,
    vognmand: true,
    galleri: true
  });
  
  const dropdownContainerRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const location = useLocation();

  const handleMouseEnter = (menuName) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(menuName);
  };

  const handleMouseLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  // Close menus on route change
  useEffect(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname, location.search]);

  // Handle outside click for dropdowns
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isEntreprenoerActive =
    location.pathname === '/ydelser/entreprenoer' ||
    location.pathname === '/ydelser/gravearbejde' ||
    location.pathname === '/ydelser/nedbrydning';

  const isBelaegningHaveActive =
    location.pathname.startsWith('/ydelser/belaegning') ||
    location.pathname === '/belaegning' ||
    location.pathname === '/belaegning-have' ||
    location.pathname === '/belaegning-og-have' ||
    location.pathname === '/ydelser/belaegning-have' ||
    location.pathname === '/ydelser/anlaegsgartner' ||
    location.pathname === '/ydelser/haveservice' ||
    location.pathname === '/ydelser/total-rydning-af-haver' ||
    location.pathname === '/ydelser/trampolin-nedgravning' ||
    location.pathname === '/have' ||
    location.pathname === '/haveservice' ||
    location.pathname === '/anlaegsgartner' ||
    location.pathname === '/total-rydning-af-haver' ||
    location.pathname === '/total-rydning' ||
    location.pathname === '/trampolin-nedgravning';

  const isVognmandActive =
    location.pathname === '/ydelser/vognmand' ||
    location.pathname === '/ydelser/bortskaffelse' ||
    location.pathname === '/ydelser/containerudlejning' ||
    location.pathname === '/ydelser/materialelevering' ||
    location.pathname === '/vognmand' ||
    location.pathname === '/containerudlejning' ||
    location.pathname === '/bortskaffelse' ||
    location.pathname === '/levering' ||
    location.pathname === '/materialelevering';

  const isGalleryActive = location.pathname.startsWith('/galleri') || location.pathname === '/foer-efter';

  const toggleMobileSection = (section) => {
    setMobileSectionOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <header className="sticky top-0 z-40 bg-[#121212]/95 backdrop-blur-md border-b border-zinc-800 shadow-xl" ref={dropdownContainerRef}>
      {/* Top info strip */}
      <div className="bg-[#0a0a0a] border-b border-zinc-800/80 text-zinc-300 text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1.5 text-zinc-400">
              <i className="fa-solid fa-location-dot text-[#F4AF00]" aria-hidden="true" />
              <span>{COMPANY_INFO.address}, {COMPANY_INFO.zipCity}</span>
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-zinc-400">
              <i className="fa-solid fa-id-card text-[#F4AF00]" aria-hidden="true" />
              <span>CVR: {COMPANY_INFO.cvr}</span>
            </span>
          </div>

          <div className="flex items-center gap-4">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="nowrap-contact inline-flex items-center gap-1.5 text-zinc-200 hover:text-[#F4AF00] font-bold transition-colors"
            >
              <i className="fa-solid fa-phone text-[#F4AF00]" aria-hidden="true" />
              <span>Tlf: {COMPANY_INFO.phone}</span>
            </a>
            <a
              href={`mailto:${COMPANY_INFO.email}`}
              className="nowrap-contact hidden md:inline-flex items-center gap-1.5 text-zinc-300 hover:text-[#F4AF00] font-medium transition-colors"
            >
              <i className="fa-solid fa-envelope text-[#F4AF00]" aria-hidden="true" />
              <span>{COMPANY_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        {/* Logo / Brand */}
        <NavLink to="/" className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-lg p-1">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=90,format=auto/assets/dde638d5-f83c-4036-8d58-4f922ae20635/image_f313bd29.png"
            alt="CMSNord.dk"
            className="h-11 sm:h-14 md:h-16 w-auto object-contain transition-transform"
          />
        </NavLink>

        {/* Desktop Navigation (Visible on screen width >= 1100px) */}
        <nav className="hidden min-[1150px]:flex items-center gap-1" aria-label="Hovednavigation">
          {/* ENTREPRENØR (med dropdown) */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('entreprenoer')}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex items-center">
              <NavLink
                to="/ydelser/entreprenoer"
                className={`px-3 py-2 text-xs xl:text-sm font-extrabold uppercase tracking-wider transition-all rounded-sm flex items-center gap-1.5 ${
                  isEntreprenoerActive
                    ? 'bg-[#F4AF00] text-[#121212]'
                    : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <span>Entreprenør</span>
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${activeDropdown === 'entreprenoer' ? 'rotate-180' : ''}`} aria-hidden="true" />
              </NavLink>
            </div>

            {activeDropdown === 'entreprenoer' && (
              <div 
                className="absolute left-0 top-full pt-1.5 w-64 z-50 animate-fade-in"
                onMouseEnter={() => handleMouseEnter('entreprenoer')}
                onMouseLeave={handleMouseLeave}
              >
                <div className="bg-[#18181b] border border-zinc-700 shadow-2xl p-2 rounded-sm">
                  <NavLink
                    to="/ydelser/gravearbejde"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-semibold transition-colors ${
                        isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4AF00]" />
                    <span>jord-/Gravearbejde</span>
                  </NavLink>
                  <NavLink
                    to="/ydelser/nedbrydning"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-semibold transition-colors ${
                        isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4AF00]" />
                    <span>Nedbrydning arbejde</span>
                  </NavLink>
                </div>
              </div>
            )}
          </div>

          {/* BELÆGNING & HAVE (med dropdown) */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('belaegningHave')}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex items-center">
              <NavLink
                to="/ydelser/belaegning-have"
                className={`px-2.5 xl:px-3 py-2 text-xs xl:text-sm font-extrabold uppercase tracking-wider transition-all rounded-sm flex items-center gap-1.5 ${
                  isBelaegningHaveActive
                    ? 'bg-[#F4AF00] text-[#121212]'
                    : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <span>Belægning &amp; Have</span>
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${activeDropdown === 'belaegningHave' ? 'rotate-180' : ''}`} aria-hidden="true" />
              </NavLink>
            </div>

            {activeDropdown === 'belaegningHave' && (
              <div 
                className="absolute left-0 top-full pt-1.5 w-64 z-50 animate-fade-in"
                onMouseEnter={() => handleMouseEnter('belaegningHave')}
                onMouseLeave={handleMouseLeave}
              >
                <div className="bg-[#18181b] border border-zinc-700 shadow-2xl p-2 rounded-sm">
                  <NavLink
                    to="/ydelser/belaegning"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-semibold transition-colors ${
                        isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4AF00]" />
                    <span>Belægnings arbejde</span>
                  </NavLink>
                  <NavLink
                    to="/ydelser/anlaegsgartner"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-semibold transition-colors ${
                        isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4AF00]" />
                    <span>Anlægning af have</span>
                  </NavLink>
                  <NavLink
                    to="/ydelser/total-rydning-af-haver"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-semibold transition-colors ${
                        isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4AF00]" />
                    <span>Rydning af haver</span>
                  </NavLink>
                  <NavLink
                    to="/ydelser/trampolin-nedgravning"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-semibold transition-colors ${
                        isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4AF00]" />
                    <span>Trampolin nedgravning</span>
                  </NavLink>
                </div>
              </div>
            )}
          </div>

          {/* VOGNMANDSKØRSEL (med dropdown) */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('vognmand')}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex items-center">
              <NavLink
                to="/ydelser/vognmand"
                className={`px-3 py-2 text-xs xl:text-sm font-extrabold uppercase tracking-wider transition-all rounded-sm flex items-center gap-1.5 ${
                  isVognmandActive
                    ? 'bg-[#F4AF00] text-[#121212]'
                    : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <span>Vognmandskørsel</span>
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${activeDropdown === 'vognmand' ? 'rotate-180' : ''}`} aria-hidden="true" />
              </NavLink>
            </div>

            {activeDropdown === 'vognmand' && (
              <div 
                className="absolute left-0 top-full pt-1.5 w-72 z-50 animate-fade-in"
                onMouseEnter={() => handleMouseEnter('vognmand')}
                onMouseLeave={handleMouseLeave}
              >
                <div className="bg-[#18181b] border border-zinc-700 shadow-2xl p-2 rounded-sm">
                  <NavLink
                    to="/ydelser/containerudlejning"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-semibold transition-colors ${
                        isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4AF00]" />
                    <span>Container udlejning</span>
                  </NavLink>
                  <NavLink
                    to="/ydelser/bortskaffelse"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-semibold transition-colors ${
                        isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4AF00]" />
                    <span>Bortskaffelse</span>
                  </NavLink>
                  <NavLink
                    to="/ydelser/materialelevering"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-semibold transition-colors ${
                        isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4AF00]" />
                    <span>Levering</span>
                  </NavLink>
                </div>
              </div>
            )}
          </div>

          {/* GALLERI (med dropdown) */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('galleri')}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex items-center">
              <NavLink
                to="/galleri"
                className={`px-3 py-2 text-xs xl:text-sm font-extrabold uppercase tracking-wider transition-all rounded-sm flex items-center gap-1.5 ${
                  isGalleryActive
                    ? 'bg-[#F4AF00] text-[#121212]'
                    : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <span>Galleri</span>
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${activeDropdown === 'galleri' ? 'rotate-180' : ''}`} aria-hidden="true" />
              </NavLink>
            </div>

            {activeDropdown === 'galleri' && (
              <div 
                className="absolute left-0 top-full pt-1.5 w-64 z-50 animate-fade-in"
                onMouseEnter={() => handleMouseEnter('galleri')}
                onMouseLeave={handleMouseLeave}
              >
                <div className="bg-[#18181b] border border-zinc-700 shadow-2xl p-2 rounded-sm">
                  <NavLink
                    to="/galleri"
                    end
                    className={({ isActive }) =>
                      `block px-3 py-2 text-xs xl:text-sm font-bold uppercase tracking-wider transition-colors ${
                        isActive && !location.search ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800 hover:text-[#F4AF00]'
                      }`
                    }
                  >
                    Alle Billeder
                  </NavLink>
                  <div className="h-px bg-zinc-700/80 my-1" />
                  {MAIN_GALLERIES.map((g) => (
                    <NavLink
                      key={g.slug}
                      to={`/galleri?kategori=${g.slug}`}
                      className="flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-medium text-zinc-300 hover:bg-zinc-800 hover:text-[#F4AF00] transition-colors"
                    >
                      <i className={`${g.icon} text-xs text-[#F4AF00]`} aria-hidden="true" />
                      <span>{g.title}</span>
                    </NavLink>
                  ))}
                  <div className="h-px bg-zinc-700/80 my-1" />
                  <NavLink
                    to="/galleri/foer-efter"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-xs xl:text-sm font-bold transition-colors ${
                        isActive
                          ? 'bg-[#F4AF00] text-[#121212]'
                          : 'text-[#F4AF00] hover:bg-zinc-800 hover:text-white'
                      }`
                    }
                  >
                    <i className="fa-solid fa-arrows-left-right text-xs" aria-hidden="true" />
                    <span>Før &amp; Efter</span>
                  </NavLink>
                </div>
              </div>
            )}
          </div>

          {/* OM OS */}
          <NavLink
            to="/om-cmsnord"
            className={({ isActive }) =>
              `px-3 py-2 text-xs xl:text-sm font-extrabold uppercase tracking-wider transition-all rounded-sm ${
                isActive
                  ? 'bg-[#F4AF00] text-[#121212]'
                  : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
              }`
            }
          >
            Om os
          </NavLink>

          {/* KONTAKT */}
          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `px-3 py-2 text-xs xl:text-sm font-extrabold uppercase tracking-wider transition-all rounded-sm ${
                isActive
                  ? 'bg-[#F4AF00] text-[#121212]'
                  : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
              }`
            }
          >
            Kontakt
          </NavLink>
        </nav>

        {/* Mobile Hamburger Button (Visible on screens < 1150px) */}
        <div className="flex min-[1150px]:hidden items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2.5 rounded-sm bg-zinc-800 text-zinc-100 hover:text-[#F4AF00] hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
            aria-label="Åbn menu"
          >
            <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="min-[1150px]:hidden bg-[#121212] border-t border-zinc-800 px-4 py-6 space-y-4 max-h-[85vh] overflow-y-auto animate-fade-in">
          {/* Quick Call Button on Mobile */}
          <a
            href={`tel:${COMPANY_INFO.phoneRaw}`}
            className="w-full flex items-center justify-center gap-2 py-3 bg-[#F4AF00] text-[#121212] font-black text-sm uppercase tracking-wider rounded-sm shadow-md"
          >
            <i className="fa-solid fa-phone" aria-hidden="true" />
            <span>Ring {COMPANY_INFO.phone}</span>
          </a>

          {/* Forside */}
          <NavLink
            to="/"
            end
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block px-3.5 py-2.5 rounded-sm text-sm font-extrabold uppercase tracking-wider transition-colors ${
                isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800'
              }`
            }
          >
            Forside
          </NavLink>

          {/* Entreprenør Section */}
          <div className="border border-zinc-800 rounded-sm bg-zinc-900/50 overflow-hidden">
            <div className="flex items-center justify-between px-3.5 py-2.5 bg-zinc-800/60">
              <NavLink
                to="/ydelser/entreprenoer"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-extrabold uppercase tracking-wider text-zinc-100 hover:text-[#F4AF00]"
              >
                Entreprenør
              </NavLink>
              <button
                type="button"
                onClick={() => toggleMobileSection('entreprenoer')}
                className="text-zinc-400 hover:text-white p-1"
                aria-label="Fold ud"
              >
                <i className={`fa-solid fa-chevron-${mobileSectionOpen.entreprenoer ? 'up' : 'down'} text-xs`} />
              </button>
            </div>
            {mobileSectionOpen.entreprenoer && (
              <div className="p-2 space-y-1 bg-zinc-900/90 text-sm">
                <NavLink
                  to="/ydelser/gravearbejde"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • jord-/Gravearbejde
                </NavLink>
                <NavLink
                  to="/ydelser/nedbrydning"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • Nedbrydning arbejde
                </NavLink>
              </div>
            )}
          </div>

          {/* Belægning & Have Section */}
          <div className="border border-zinc-800 rounded-sm bg-zinc-900/50 overflow-hidden">
            <div className="flex items-center justify-between px-3.5 py-2.5 bg-zinc-800/60">
              <NavLink
                to="/ydelser/belaegning-have"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-extrabold uppercase tracking-wider text-zinc-100 hover:text-[#F4AF00]"
              >
                Belægning &amp; Have
              </NavLink>
              <button
                type="button"
                onClick={() => toggleMobileSection('belaegningHave')}
                className="text-zinc-400 hover:text-white p-1"
                aria-label="Fold ud"
              >
                <i className={`fa-solid fa-chevron-${mobileSectionOpen.belaegningHave ? 'up' : 'down'} text-xs`} />
              </button>
            </div>
            {mobileSectionOpen.belaegningHave && (
              <div className="p-2 space-y-1 bg-zinc-900/90 text-sm">
                <NavLink
                  to="/ydelser/belaegning"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • Belægnings arbejde
                </NavLink>
                <NavLink
                  to="/ydelser/anlaegsgartner"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • Anlægning af have
                </NavLink>
                <NavLink
                  to="/ydelser/total-rydning-af-haver"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • Rydning af haver
                </NavLink>
                <NavLink
                  to="/ydelser/trampolin-nedgravning"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • Trampolin nedgravning
                </NavLink>
              </div>
            )}
          </div>

          {/* Vognmandskørsel Section */}
          <div className="border border-zinc-800 rounded-sm bg-zinc-900/50 overflow-hidden">
            <div className="flex items-center justify-between px-3.5 py-2.5 bg-zinc-800/60">
              <NavLink
                to="/ydelser/vognmand"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-extrabold uppercase tracking-wider text-zinc-100 hover:text-[#F4AF00]"
              >
                Vognmandskørsel
              </NavLink>
              <button
                type="button"
                onClick={() => toggleMobileSection('vognmand')}
                className="text-zinc-400 hover:text-white p-1"
                aria-label="Fold ud"
              >
                <i className={`fa-solid fa-chevron-${mobileSectionOpen.vognmand ? 'up' : 'down'} text-xs`} />
              </button>
            </div>
            {mobileSectionOpen.vognmand && (
              <div className="p-2 space-y-1 bg-zinc-900/90 text-sm">
                <NavLink
                  to="/ydelser/containerudlejning"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • Container udlejning
                </NavLink>
                <NavLink
                  to="/ydelser/bortskaffelse"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • Bortskaffelse
                </NavLink>
                <NavLink
                  to="/ydelser/materialelevering"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • Levering
                </NavLink>
              </div>
            )}
          </div>

          {/* Galleri Section */}
          <div className="border border-zinc-800 rounded-sm bg-zinc-900/50 overflow-hidden">
            <div className="flex items-center justify-between px-3.5 py-2.5 bg-zinc-800/60">
              <NavLink
                to="/galleri"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-extrabold uppercase tracking-wider text-zinc-100 hover:text-[#F4AF00]"
              >
                Galleri
              </NavLink>
              <button
                type="button"
                onClick={() => toggleMobileSection('galleri')}
                className="text-zinc-400 hover:text-white p-1"
                aria-label="Fold ud"
              >
                <i className={`fa-solid fa-chevron-${mobileSectionOpen.galleri ? 'up' : 'down'} text-xs`} />
              </button>
            </div>
            {mobileSectionOpen.galleri && (
              <div className="p-2 space-y-1 bg-zinc-900/90 text-sm">
                <NavLink
                  to="/galleri"
                  end
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 text-zinc-300 hover:text-[#F4AF00]"
                >
                  • Alle Billeder
                </NavLink>
                {MAIN_GALLERIES.map((g) => (
                  <NavLink
                    key={g.slug}
                    to={`/galleri?kategori=${g.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-1.5 text-zinc-400 hover:text-[#F4AF00]"
                  >
                    • {g.title}
                  </NavLink>
                ))}
                <NavLink
                  to="/galleri/foer-efter"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-1.5 font-bold text-[#F4AF00] hover:text-white"
                >
                  • Før &amp; Efter
                </NavLink>
              </div>
            )}
          </div>

          {/* Om os */}
          <NavLink
            to="/om-cmsnord"
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block px-3.5 py-2.5 rounded-sm text-sm font-extrabold uppercase tracking-wider transition-colors ${
                isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800'
              }`
            }
          >
            Om os
          </NavLink>

          {/* Kontakt */}
          <NavLink
            to="/kontakt"
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block px-3.5 py-2.5 rounded-sm text-sm font-extrabold uppercase tracking-wider transition-colors ${
                isActive ? 'bg-[#F4AF00] text-[#121212]' : 'text-zinc-200 hover:bg-zinc-800'
              }`
            }
          >
            Kontakt
          </NavLink>
        </div>
      )}
    </header>
  );
}
